/**
 * Billing-profile helpers.
 *
 * A user's fiscal identity — who the invoice is made out to — lives in
 * `privateData`, as **flat fields**. It is a separate axis from
 * `publicData.userType`, which encodes the marketplace role (host/customer,
 * written from the signup form's `whatToDo`): a host can be a company and a
 * bañista can be a company, and either can be a private individual.
 *
 * It sits in `privateData` on purpose. A transaction's `protectedData` and
 * `metadata` are both readable by BOTH participants, so freezing a billing
 * snapshot there would hand each party the other's tax id and fiscal address —
 * see docs/contact-info-privacy.md. `privateData` is readable by the user and by
 * the Integration API, which is exactly who issues the invoices.
 *
 * Shape:
 *   privateData = {
 *     billingEntityType: 'individual' | 'company',
 *     dni_nie_number: '12345678Z',      // the PERSON's document. Always.
 *     razonSocial: 'ACME Piscinas SL',  // company only
 *     cif: 'B09857830',                 // company only
 *     billingAddressLine1, billingAddressLine2,
 *     billingPostalCode, billingCity, billingProvince, billingCountry,
 *     invoiceEmail: 'facturas@acme.es', // optional; falls back to the account email
 *     billingUpdatedAt: '2026-09-10T10:00:00.000Z',
 *   }
 *
 * **There is exactly one field per real-world fact, and that is the whole
 * point.** An earlier version nested all of this under a `billing` object which
 * carried its own `legalName` and `taxId` — meaning a private individual's
 * document was stored twice, once as `dni_nie_number` and once as
 * `billing.taxId`. Two copies of one fact drift: an operator fixing a typo in
 * Sharetribe Console would fix the insurer's notice and leave the invoice on the
 * stale value, and the next time the user saved this page the correction was
 * overwritten. Both failures were silent. Flat fields also edit properly in
 * Console, where a nested object is a JSON blob.
 *
 * Which fields make up the invoice is **inferred from `billingEntityType`**, and
 * that inference is the contract with `cocopool-invoices-service`:
 *
 *   individual -> legalName = firstName + lastName, taxId = dni_nie_number
 *   company    -> legalName = razonSocial,          taxId = cif
 *
 * See docs/billing-details.md.
 */

export const INDIVIDUAL = 'individual';
export const COMPANY = 'company';

export const BILLING_ENTITY_TYPES = [INDIVIDUAL, COMPANY];

const DEFAULT_COUNTRY = 'ES';

const trimmed = value => (typeof value === 'string' ? value.trim() : '');

/**
 * Normalise a tax id for storage and comparison: upper-cased, with spaces and
 * the dashes people habitually type in a NIE stripped out.
 */
export const normalizeTaxId = value =>
  trimmed(value)
    .toUpperCase()
    .replace(/[\s.-]/g, '');

/**
 * ¿Es extranjero el documento guardado? Solo sirve para precargar la casilla
 * "Mi documento no es español", que no se guarda en ningún sitio.
 *
 * Se mira la FORMA, no el dígito de control, a propósito: un DNI español con la
 * letra mal, o sin ella, tiene que llegar con la casilla desmarcada para que el
 * formulario lo señale, y no colarse otra vez como "extranjero".
 */
const SPANISH_DOCUMENT_SHAPE = /^([XYZKLM]?\d{7,8}[A-Z]?|[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J])$/;

export const isForeignDocument = value => {
  const normalized = normalizeTaxId(value);
  return !!normalized && !SPANISH_DOCUMENT_SHAPE.test(normalized);
};

/**
 * The address a legacy account still carries in `publicData.userAddress`.
 *
 * The profile stopped writing it — a fiscal address has no business being
 * world-readable — so this only ever feeds accounts that have not saved the
 * billing page since. Note the key is `provence`, a long-standing typo at the
 * source; ours spells it `province`.
 */
const legacyAddress = profile => {
  const { street, apartmentNumber, city, provence, postalCode } =
    profile?.publicData?.userAddress || {};

  return {
    line1: [trimmed(street), trimmed(apartmentNumber)].filter(Boolean).join(', '),
    line2: '',
    postalCode: trimmed(postalCode),
    city: trimmed(city),
    province: trimmed(provence),
    country: DEFAULT_COUNTRY,
  };
};

/**
 * The person's identity document.
 *
 * Lives in `privateData` now; `publicData` is read for accounts that predate the
 * move. This is what the insurer cron prints under "DNI/NIE anfitrión", and for
 * a private individual it is also their tax id — the same field, not a copy.
 */
export const personalDniOf = profile =>
  normalizeTaxId(profile?.privateData?.dni_nie_number || profile?.publicData?.dni_nie_number);

/**
 * Resolve who an invoice is made out to.
 *
 * Never returns null. Which fields are used is inferred from
 * `billingEntityType`, so an account that never declared anything resolves as an
 * individual billed with the name and DNI already on their profile — the same
 * fallback the invoices service applies.
 *
 * @param {Object} user a denormalised user / currentUser entity
 * @returns {Object} { entityType, legalName, taxId, personalDni, address, invoiceEmail }
 */
export const resolveBillingProfile = user => {
  const profile = user?.attributes?.profile || {};
  const privateData = profile.privateData || {};

  const entityType = privateData.billingEntityType === COMPANY ? COMPANY : INDIVIDUAL;
  const isCompany = entityType === COMPANY;
  const personalDni = personalDniOf(profile);

  const ownName = [trimmed(profile.firstName), trimmed(profile.lastName)].filter(Boolean).join(' ');

  // The address is taken as a BLOCK — all of ours or all of the legacy one.
  // Mixing them would give a postal code from one and a city from the other.
  const line1 = trimmed(privateData.billingAddressLine1);
  const address = line1
    ? {
        line1,
        line2: trimmed(privateData.billingAddressLine2),
        postalCode: trimmed(privateData.billingPostalCode),
        city: trimmed(privateData.billingCity),
        province: trimmed(privateData.billingProvince),
        country: trimmed(privateData.billingCountry) || DEFAULT_COUNTRY,
      }
    : legacyAddress(profile);

  return {
    entityType,
    // A company bills under its razón social and its CIF; a private individual
    // under their own name and their own document. No third copy of either.
    legalName: isCompany ? trimmed(privateData.razonSocial) : ownName,
    taxId: isCompany ? normalizeTaxId(privateData.cif) : personalDni,
    personalDni,
    address,
    invoiceEmail: trimmed(privateData.invoiceEmail) || trimmed(user?.attributes?.email),
  };
};

/**
 * Has the user ever been ASKED which they are?
 *
 * Not the same question as `resolveBillingProfile(user).entityType === INDIVIDUAL`.
 * An account that was never asked has no `billingEntityType` and resolves as an
 * individual **by default, silently** — so a host who is really a company passes
 * every completeness check and keeps being invoiced under a person's name.
 */
export const hasDeclaredBillingIdentity = user =>
  !!user?.attributes?.profile?.privateData?.billingEntityType;

/**
 * Qué le falta a un usuario para que su factura salga bien.
 *
 * Devuelve las claves que faltan, en el orden en que aparecen en el formulario,
 * para poder decirle a la gente qué tiene que rellenar en vez de un "revisa tus
 * datos" genérico. Vacío quiere decir que no falta nada.
 *
 * **Es más estricta que `isBillingProfileComplete` a propósito**, y las dos
 * conviven: aquella decide si el checkout enseña los campos de empresa en línea,
 * y por eso no le exige dirección a un particular. Esta mira lo que necesita una
 * factura, y una factura española lleva dirección sea quien sea el destinatario.
 * No se unifican porque cambiar la primera cambiaría el checkout.
 */
export const missingBillingFields = user => {
  const { legalName, taxId, address } = resolveBillingProfile(user);

  return [
    !legalName ? 'legalName' : null,
    !taxId ? 'taxId' : null,
    !address.line1 ? 'addressLine1' : null,
    !address.postalCode ? 'postalCode' : null,
    !address.city ? 'city' : null,
  ].filter(Boolean);
};

/**
 * Is the user's billing profile invoiceable?
 *
 * Everyone needs a name and a tax id: an invoice with no NIF is not an invoice.
 * The **fiscal address** is only demanded of companies — bañistas have an
 * optional address by design, so requiring it of everyone would turn this into a
 * mass nag. This is what drives the inline company fields at checkout.
 */
export const isBillingProfileComplete = user => {
  const { entityType, legalName, taxId, address } = resolveBillingProfile(user);
  if (!legalName || !taxId) {
    return false;
  }
  if (entityType !== COMPANY) {
    return true;
  }
  return !!(address.line1 && address.postalCode && address.city);
};

/**
 * Convierte un anuncio en los campos del formulario de facturación.
 *
 * La dirección exacta vive en `privateData.location` (solo la ve su autor); se
 * cae a `publicData.location` para los anuncios anteriores a esa separación —
 * ver docs/listing-location-privacy.md.
 *
 * Un anuncio **no guarda código postal**: viene dentro de la cadena que devuelve
 * Mapbox ("Carrer Gran 12, 08301 Mataró"). Se extrae de ahí y se quita del resto
 * para que no salga duplicado. Es una heurística, y por eso el usuario ve el
 * resultado en el formulario antes de guardar.
 */
export const addressFromListing = listing => {
  const { publicData, privateData } = listing?.attributes || {};
  const location = privateData?.location || publicData?.location || {};
  const raw = typeof location.address === 'string' ? location.address.trim() : '';

  if (!raw) {
    return null;
  }

  const city = typeof publicData?.city === 'string' ? publicData.city.trim() : '';
  const province = typeof publicData?.province === 'string' ? publicData.province.trim() : '';

  // Cinco dígitos sueltos: un código postal español. Sin `g`, para quedarse con
  // el primero y no confundirse con un número largo de portal.
  const postalCode = (raw.match(/\b(\d{5})\b/) || [])[1] || '';

  // De la cadena se quitan el CP, la ciudad y la provincia, que van en sus
  // propios campos; lo que queda es la calle.
  const line1 = [postalCode, city, province]
    .filter(Boolean)
    .reduce((acc, part) => acc.split(part).join(''), raw)
    .replace(/\s*,\s*/g, ', ')
    .replace(/(,\s*)+/g, ', ')
    .replace(/^[\s,]+|[\s,]+$/g, '')
    .trim();

  return { line1: line1 || raw, postalCode, city, province };
};

/**
 * Los valores con los que se precarga el formulario de cobros de Stripe.
 *
 * Los datos fiscales son los mismos que los de la página de facturación, así que
 * el anfitrión no tiene que volver a teclearlos.
 *
 * Todos los valores son primitivos a propósito: `initialValues` se construye en
 * cada render y react-final-form decide si reinicializar con un `shallowEqual`,
 * que con un objeto anidado nunca daría igual y reiniciaría el formulario
 * mientras se escribe.
 */
export const stripeAccountInitialValues = user => {
  const profile = user?.attributes?.profile || {};
  const privateData = profile.privateData || {};
  const isCompany = privateData.billingEntityType === COMPANY;
  const personalDni = personalDniOf(profile);
  const birthday = trimmed(privateData.birthday);

  return {
    accountType: isCompany ? COMPANY : INDIVIDUAL,
    // Con empresa este campo no se pinta: lo pide `companyOwnerDni`.
    dni: isCompany ? '' : personalDni,
    dniForeign: !isCompany && isForeignDocument(personalDni),
    companyLegalName: isCompany ? trimmed(privateData.razonSocial) : '',
    companyTaxId: isCompany ? normalizeTaxId(privateData.cif) : '',
    companyOwnerDni: isCompany ? personalDni : '',
    companyOwnerDniForeign: isCompany && isForeignDocument(personalDni),
    // Solo si ya está en el formato que el campo entiende; si no, en blanco.
    dateOfBirth: /^\d{2}\/\d{2}\/\d{4}$/.test(birthday) ? birthday : '',
  };
};

/**
 * Adopt the individual/company answer the host already gave to Stripe.
 *
 * `StripeConnectAccountForm` asks it outright, with radio values that happen to
 * be exactly our own (`'individual'` / `'company'`), and a company is asked for
 * its razón social, its CIF and the owner's DNI besides. This is the cheapest
 * declaration we can get, so it is taken — but it never overwrites what the user
 * typed on the billing page.
 *
 * @returns {Object|null} the `privateData` fragment to merge, or null
 */
export const billingFromStripeAccountType = (values, user) => {
  const {
    accountType,
    dni,
    companyLegalName,
    companyTaxId,
    companyOwnerDni,
    useListingAddress,
    listingAddress,
  } = values || {};
  const privateData = user?.attributes?.profile?.privateData || {};

  // Ya declaró por su cuenta: no se pisa.
  if (trimmed(privateData.razonSocial) || trimmed(privateData.cif)) {
    return null;
  }

  const isCompany = accountType === COMPANY;
  const personalDni = normalizeTaxId(isCompany ? companyOwnerDni : dni);
  const cif = isCompany ? normalizeTaxId(companyTaxId) : '';
  const razonSocial = isCompany ? trimmed(companyLegalName) : '';

  // Sin nada que declarar no se escribe: un `billingEntityType` suelto haría que
  // `hasDeclaredBillingIdentity` mintiera.
  if (!personalDni && !cif && !razonSocial) {
    return null;
  }

  // La dirección del anuncio, solo si el anfitrión la ha aceptado. Va
  // desmarcada a propósito: la piscina no tiene por qué estar donde su dueño
  // tiene el domicilio fiscal, y una dirección que nadie eligió es peor que
  // ninguna — el aviso del panel la pide, y ahí se ve lo que se escribe.
  const address =
    useListingAddress && listingAddress?.line1
      ? {
          billingAddressLine1: trimmed(listingAddress.line1),
          billingPostalCode: trimmed(listingAddress.postalCode),
          billingCity: trimmed(listingAddress.city),
          billingProvince: trimmed(listingAddress.province),
          billingCountry: DEFAULT_COUNTRY,
        }
      : {};

  return {
    billingEntityType: isCompany ? COMPANY : INDIVIDUAL,
    // El documento de la PERSONA. En una cuenta de empresa Stripe no nos lo da
    // —verifica al representante por su cuenta—, así que se pide aparte; sin él
    // el aviso al asegurador saldría con un guion donde va el DNI.
    ...(personalDni ? { dni_nie_number: personalDni } : {}),
    ...(isCompany ? { razonSocial, cif } : {}),
    ...address,
    billingUpdatedAt: new Date().toISOString(),
  };
};

/**
 * Build the `privateData` fragment the billing form persists.
 *
 * The form speaks in `legalName` / `taxId` because that is what the user is
 * being asked for; storage speaks in the fields those mean for this kind of
 * taxpayer. That mapping happens here and nowhere else.
 *
 * For a company, `razonSocial` and `cif` are the sociedad's, and
 * `dni_nie_number` stays the administrator's own document — two different
 * identities that must never be forced to agree. For a private individual there
 * is no second copy at all: their document IS their tax id.
 *
 * @param {Object} values the submitted form values
 * @returns {Object} a `privateData` fragment; `null` values delete the key
 */
export const buildBillingPrivateData = values => {
  const { entityType, legalName, taxId, personalDni, invoiceEmail, address } = values || {};
  const isCompany = entityType === COMPANY;

  return {
    billingEntityType: isCompany ? COMPANY : INDIVIDUAL,
    dni_nie_number: normalizeTaxId(isCompany ? personalDni : taxId),
    // `null` borra la clave en Sharetribe, así que un CIF no sobrevive a pasar
    // de empresa a particular y quedarse ahí confundiendo a quien mire.
    razonSocial: isCompany ? trimmed(legalName) : null,
    cif: isCompany ? normalizeTaxId(taxId) : null,
    billingAddressLine1: trimmed(address?.line1),
    billingAddressLine2: trimmed(address?.line2),
    billingPostalCode: trimmed(address?.postalCode),
    billingCity: trimmed(address?.city),
    billingProvince: trimmed(address?.province),
    billingCountry: trimmed(address?.country) || DEFAULT_COUNTRY,
    invoiceEmail: trimmed(invoiceEmail),
    billingUpdatedAt: new Date().toISOString(),
  };
};
